import { differenceInCalendarDays } from 'date-fns';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import {
  SUBSIDY_CATEGORY_MAP,
  SubsidyCategoryType,
  SUBSIDY_CATEGORIES,
} from '../../../constants/subsidy/subsidyCategory.constant';
import { CITY_EN_MAP, STATE_CODE_MAP } from '../../../constants/subsidy/regionMapping.constant';
import { useGetSubsidiesQuery } from '../../../hooks/api/subsidy/queries/useGetSubsidies.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useScrollDirection } from '../../../hooks/scroll/useScrollDirection';
import { Icon } from '../../../components/common/icon/Icon';
import { SubsidyVerticalListView } from '../../../views/Subsidy/listView/SubsidyVerticalListView';
import { SubsidyCategoryFilter } from '../../../views/Subsidy/listView/components/SubsidyCategoryFilter/SubsidyCategoryFilter';
import { SubsidyRegionFilter } from '../../../views/Subsidy/listView/components/SubsidyRegionFilter/SubsidyRegionFilter';
import { BottomFloatingButton } from '../../../components/common/buttons/FloatingButtons/BottomFloatingButton';
import { ampli } from '../../../ampli';
import { getActiveBanners } from '../../../constants/subsidy/subsidyBanner.constant';
import { SubsidyBannerCard } from '../../../views/Subsidy/listView/components/SubsidyBannerCard/SubsidyBannerCard';
import { ISubsidyBanner } from '../../../interfaces/Banner.interfaces';
import { ISubsidy } from '../../../interfaces/Subsidy.interfaces';
import { vibrateWithDuration } from '../../../utils/vibration.util';

export const SubsidyListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleContactButtonClick = () => {
    ampli.pvmClickSubsidyContactlistButton();
    navigate('/info/0e56dfef-3186-46a9-bd7b-7e79b2441ee1');
  };

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} headerTitle={'지원사업'} hasMyInfoButton />,
    fixedHeaderHeight: 48,
    fixedFooter: (
      <BottomFloatingButton
        handleButtonClick={handleContactButtonClick}
        title={'문의처'}
        icon={<Icon name={'Phone'} width={20} height={20} fill={'#FFFFFF'} />}
      />
    ),
    fixedFooterHeight: 92,
  });
  const queryCategory = searchParams.get('category') as SubsidyCategoryType | null;
  const queryState = searchParams.get('state');
  const queryCity = searchParams.get('city');

  // Initialize from URL or default
  const [selectedState, setSelectedState] = useState(queryState ? STATE_CODE_MAP[queryState] ?? '전국' : '전국');
  const [selectedCity, setSelectedCity] = useState(queryCity ? CITY_EN_MAP[queryCity] ?? '전체' : '전체');
  const [selectedCategories, setSelectedCategories] = useState<SubsidyCategoryType[]>(
    queryCategory ? (queryCategory.split(',') as SubsidyCategoryType[]) : [...SUBSIDY_CATEGORIES],
  );
  const querySort = searchParams.get('sort') as 'newest' | 'deadline' | null;
  const [sortType, setSortType] = useState<'newest' | 'deadline' | null>(querySort);

  const handleSortChange = (type: 'newest' | 'deadline') => {
    vibrateWithDuration(20);
    ampli.pvmClickSubsidySortFilter({ sort_type: type === 'newest' ? 'NEWEST' : 'DEADLINE' });

    const newParams = new URLSearchParams(searchParams);
    if (sortType === type) {
      // Toggle off if same type is clicked (to go to default/order-based sort)
      newParams.delete('sort');
      setSortType(null);
    } else {
      newParams.set('sort', type);
      setSortType(type);
    }
    setSearchParams(newParams);
  };

  // URL Sync Effects
  useEffect(() => {
    // Sync Category from URL
    if (queryCategory) {
      setSelectedCategories(queryCategory.split(',') as SubsidyCategoryType[]);
    } else {
      setSelectedCategories([...SUBSIDY_CATEGORIES]);
    }

    // Sync State from URL
    const mappedState = queryState ? STATE_CODE_MAP[queryState] : '전국';
    setSelectedState(mappedState ?? '전국');

    // Sync City from URL
    const mappedCity = queryCity ? CITY_EN_MAP[queryCity] : '전체';
    setSelectedCity(mappedCity ?? '전체');

    // Sync Sort from URL
    setSortType(querySort);
  }, [queryCategory, queryState, queryCity, querySort]);

  const handleCategoryChange = (category: SubsidyCategoryType) => {
    let nextCategories: SubsidyCategoryType[] = [];

    if (category === '전체') {
      const isAllSelected = selectedCategories.includes('전체');
      if (isAllSelected) {
        nextCategories = []; // Clear all
      } else {
        nextCategories = [...SUBSIDY_CATEGORIES];
      }
    } else if (selectedCategories.includes(category)) {
      nextCategories = selectedCategories.filter((c) => c !== category && c !== '전체');
    } else {
      const updated = [...selectedCategories, category];
      // If all individual categories are now selected, also select 'All'
      const individualCategories = SUBSIDY_CATEGORIES.filter((c) => c !== '전체');
      const allIndividualSelected = individualCategories.every((c) => updated.includes(c));
      if (allIndividualSelected) {
        nextCategories = [...SUBSIDY_CATEGORIES];
      } else {
        nextCategories = updated;
      }
    }

    setSelectedCategories(nextCategories);

    if (nextCategories.length === 0 || nextCategories.includes('전체')) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', nextCategories.join(','));
    }
    setSearchParams(searchParams);
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setSelectedCity('전체'); // Reset city

    // Update URL
    const stateCode = STATE_CODE_MAP[state] ?? 'all';
    if (stateCode === 'all') {
      searchParams.delete('state');
      searchParams.delete('city');
    } else {
      searchParams.set('state', stateCode);
      searchParams.delete('city'); // City resets on state change
    }
    setSearchParams(searchParams);
  };

  const handleCityChange = (city: string) => {
    setSelectedCity(city);

    // Update URL
    const citySlug = CITY_EN_MAP[city] ?? 'all';
    if (citySlug === 'all') {
      searchParams.delete('city');
    } else {
      searchParams.set('city', citySlug);
    }
    setSearchParams(searchParams);
  };

  const { data: subsidies, isLoading: isGetSubsidiesQueryLoading } = useGetSubsidiesQuery();

  const { scrollDir, isAtTop } = useScrollDirection();

  const JEONBUK_VARIANTS = ['전북', '전북특별자치도'];
  const JEONNAM_VARIANTS = ['전남', '전라남도'];
  const GYEONGBUK_VARIANTS = ['경북', '경상북도'];

  const filteredSubsidies = (subsidies ?? [])
    .filter((subsidy) => {
      if (!subsidy.isReady) return false;

      // Region filtering logic
      const isJeonbukSelected = selectedState === '전북특별자치도';
      const isJeonnamSelected = selectedState === '전라남도';
      const isGyeongbukSelected = selectedState === '경상북도';
      const isStateMatch =
        selectedState === '전국' ||
        (isJeonbukSelected && JEONBUK_VARIANTS.includes(subsidy.state ?? '')) ||
        (isJeonnamSelected && JEONNAM_VARIANTS.includes(subsidy.state ?? '')) ||
        (isGyeongbukSelected && GYEONGBUK_VARIANTS.includes(subsidy.state ?? '')) ||
        subsidy.state === selectedState;

      if (!isStateMatch && !subsidy.isNationwide) return false;

      // City filtering
      if (selectedCity !== '전체') {
        const isCityMatch =
          subsidy.isNationwide ||
          (isStateMatch && (!subsidy.city || subsidy.city === '')) ||
          subsidy.city === selectedCity;

        if (!isCityMatch) return false;
      }

      // Category & tag filtering logic
      if (!selectedCategories.includes('전체')) {
        if (selectedCategories.length === 0) return false;

        const allMappedTagNames = selectedCategories.flatMap((cat) => {
          if (cat === '전체') return [];
          const tags = SUBSIDY_CATEGORY_MAP[cat as Exclude<SubsidyCategoryType, '전체'>];
          return tags.map((t) => t.name as string);
        });

        const hasMatchingTag = subsidy.tags?.some((tag) => (allMappedTagNames as string[]).includes(tag.name));
        if (!hasMatchingTag) return false;
      }

      const today = new Date();
      if (subsidy.estimateDeadline) {
        const estimateDate = new Date(subsidy.estimateDeadline);
        if (differenceInCalendarDays(estimateDate, today) < 0) return false;
      }

      // 1. ALWAYS, MULTIPLE, TBD 상태는 기한 체크 없이 통과
      if (['ALWAYS', 'MULTIPLE', 'TBD'].includes(subsidy.deadlineType ?? '')) return true;

      // 2. FIXED이거나 날짜가 있는 경우만 날짜 비교 수행
      if (!subsidy.deadline) return false;

      const deadlineDate = new Date(subsidy.deadline);
      return differenceInCalendarDays(deadlineDate, today) >= 0;
    })
    .filter((subsidy) => subsidy.deadlineType !== 'NONE')
    .sort((a, b) => {
      const today = new Date();

      if (sortType === 'deadline') {
        const getDeadlineDays = (subsidy: ISubsidy) => {
          if (['ALWAYS', 'MULTIPLE', 'TBD'].includes(subsidy.deadlineType ?? '')) return Infinity;
          if (!subsidy.deadline) return Infinity;
          return differenceInCalendarDays(new Date(subsidy.deadline), today);
        };

        const daysA = getDeadlineDays(a);
        const daysB = getDeadlineDays(b);

        if (daysA !== daysB) {
          return daysA - daysB;
        }

        // Tier 2: Order (DESC)
        const orderA = a.order ?? Number.MIN_SAFE_INTEGER;
        const orderB = b.order ?? Number.MIN_SAFE_INTEGER;
        if (orderA !== orderB) return orderB - orderA;

        // Tier 3: PostedAt (DESC)
        const dateA = a.postedAt ? new Date(a.postedAt).getTime() : 0;
        const dateB = b.postedAt ? new Date(b.postedAt).getTime() : 0;
        return dateB - dateA;
      }

      if (sortType === 'newest') {
        const dateA = a.postedAt ? new Date(a.postedAt).getTime() : 0;
        const dateB = b.postedAt ? new Date(b.postedAt).getTime() : 0;
        if (dateA !== dateB) return dateB - dateA;

        // Tier 2: Order (DESC)
        const orderA = a.order ?? Number.MIN_SAFE_INTEGER;
        const orderB = b.order ?? Number.MIN_SAFE_INTEGER;
        return orderB - orderA;
      }

      // Default Sort (null): Order (DESC) -> PostedAt (DESC)
      const orderA = a.order ?? Number.MIN_SAFE_INTEGER;
      const orderB = b.order ?? Number.MIN_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderB - orderA;
      }

      const dateA = a.postedAt ? new Date(a.postedAt).getTime() : 0;
      const dateB = b.postedAt ? new Date(b.postedAt).getTime() : 0;
      return dateB - dateA;
    });

  const activeBanners = getActiveBanners(selectedCategories);
  const itemsToRender: (ISubsidy | ISubsidyBanner)[] = [...filteredSubsidies];

  // Sort banners by displayIndex to insert correctly
  const sortedBanners = [...activeBanners].sort((a, b) => a.displayIndex - b.displayIndex);

  sortedBanners.forEach((banner) => {
    const insertIndex = Math.min(banner.displayIndex, itemsToRender.length);
    itemsToRender.splice(insertIndex, 0, banner);
  });

  return (
    <>
      <Helmet>
        <title>{pageInfo.subsidyListPage.title}</title>
      </Helmet>

      <Container>
        <StickyFilterWrapper isVisible={scrollDir === 'up'} isAtTop={isAtTop}>
          <SubsidyRegionFilter
            selectedState={selectedState}
            selectedCity={selectedCity}
            onStateChange={handleStateChange}
            onCityChange={handleCityChange}
          />

          <SubsidyCategoryFilter selectedCategories={selectedCategories} onCategoryChange={handleCategoryChange} />

          <SortWrapper>
            <SortButton isActive={sortType === 'newest'} onClick={() => handleSortChange('newest')}>
              {'최신 순'}
              <Icon
                name={'AngleDown'}
                width={16}
                height={16}
                fill={sortType === 'newest' ? '#232429' : '#898B94'}
                style={{
                  transition: 'transform 0.2s',
                  transform: sortType === 'newest' ? 'rotate(0deg)' : 'rotate(0deg)',
                }}
              />
            </SortButton>
            <SortButton isActive={sortType === 'deadline'} onClick={() => handleSortChange('deadline')}>
              {'마감일 순'}
            </SortButton>
          </SortWrapper>
        </StickyFilterWrapper>

        {isGetSubsidiesQueryLoading && <AnimateLoading />}

        {!isGetSubsidiesQueryLoading && itemsToRender.length > 0 && (
          <ListWrapper>
            {itemsToRender.map((item) => {
              if ('showBanner' in item) {
                return <SubsidyBannerCard key={item.id} banner={item} city={selectedCity} state={selectedState} />;
              }
              return <SubsidyVerticalListView key={item.id} subsidy={item} />;
            })}
          </ListWrapper>
        )}

        {!isGetSubsidiesQueryLoading && itemsToRender.length === 0 && (
          <EmptyWrapper>
            <Icon name={'EmptyBox2'} width={160} height={160} />
            <EmptyTextSection>
              <EmptyTitle>{'앗! 내 지역 공고가 아직 없어요.'}</EmptyTitle>
            </EmptyTextSection>
          </EmptyWrapper>
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 16px;

  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 140px);
`;

const EmptyWrapper = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  gap: 12px;
  padding-bottom: 80px;
`;

const EmptyTextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 4px;
`;

const EmptyTitle = styled.h2`
  color: var(--light-scheme-monochrome-offblack, #505363);
  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const StickyFilterWrapper = styled.div<{ isVisible: boolean; isAtTop: boolean }>`
  position: sticky;
  top: 48px;
  z-index: 10;
  background-color: #fff;
  margin: -16px -16px 0 -16px;
  padding: 16px 16px 0 16px;

  transition: transform 0.3s ease-in-out;
  transform: translateY(${(props) => (props.isVisible || props.isAtTop ? '0' : '-110%')});

  box-shadow: ${(props) => (!props.isAtTop ? '0 4px 12px rgba(0, 0, 0, 0.05)' : 'none')};
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const SortWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  margin-bottom: 12px;
`;

const SortButton = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  color: ${({ isActive }) => (isActive ? '#232429' : '#898B94')};
  font-family: Pretendard;
  font-size: 14px;
  font-weight: ${({ isActive }) => (isActive ? '700' : '500')};
`;
